import { AI_CHAT_MESSAGE_KIND, AI_CHAT_MESSAGE_ROLE } from '@portal/common/models/ai-chat-message';
import {
    AiChatDataCollectionOption,
    AiChatFeedbackReaction,
    AiChatFilterSet,
    ChartType,
    DataQueryParams,
    DataQueryType,
    DataResponse,
    OperationStatus,
} from '@portal/common/types';

// In-memory chat messages (Redux state)

interface AiChatFeedbackableMessageBase {
    id: string;
    reaction: AiChatFeedbackReaction | null;
    dataCollectionId: number | null;
}

export interface AiChatUserTextMessage {
    id: string;
    role: typeof AI_CHAT_MESSAGE_ROLE.USER;
    kind: typeof AI_CHAT_MESSAGE_KIND.TEXT;
    content: string;
}

export interface AiChatAssistantTextMessage extends AiChatFeedbackableMessageBase {
    role: typeof AI_CHAT_MESSAGE_ROLE.ASSISTANT;
    kind: typeof AI_CHAT_MESSAGE_KIND.TEXT;
    content: string;
}

export type AiChatTextMessage = AiChatUserTextMessage | AiChatAssistantTextMessage;

export interface AiChatFiltersMessage extends AiChatFeedbackableMessageBase, AiChatFilterSet {
    role: typeof AI_CHAT_MESSAGE_ROLE.ASSISTANT;
    kind: typeof AI_CHAT_MESSAGE_KIND.FILTERS;
}

// Ranking/aggregation (and future data query types): carries the request needed to
// fetch the DataResponse plus the async fetch state. `data` is filled in by an epic.
export interface AiChatQueryResultMessage extends AiChatFeedbackableMessageBase, AiChatFilterSet {
    role: typeof AI_CHAT_MESSAGE_ROLE.ASSISTANT;
    kind: typeof AI_CHAT_MESSAGE_KIND.QUERY_RESULT;
    queryType: DataQueryType;
    params: DataQueryParams;
    status: OperationStatus;
    data: DataResponse | null;
    error: string | null;
    dataCollectionOptions: AiChatDataCollectionOption[];
}

export interface AiChatDataCollectionPickerMessage {
    id: string;
    role: typeof AI_CHAT_MESSAGE_ROLE.ASSISTANT;
    kind: typeof AI_CHAT_MESSAGE_KIND.DATA_COLLECTION_PICKER;
    content: string | null;
    responseId: string;
    options: AiChatDataCollectionOption[];
    queryType?: DataQueryType;
    params: DataQueryParams;
    visualization?: ChartType;
    pickedDataCollectionId: number | null;
    isDismissed: boolean;
}

export type AiChatDataCollectionPickerPayload = Pick<
    AiChatDataCollectionPickerMessage,
    'content' | 'responseId' | 'options' | 'queryType' | 'params' | 'visualization'
>;

export interface AiChatManualFiltersMessage extends AiChatFilterSet {
    id: string;
    role: typeof AI_CHAT_MESSAGE_ROLE.MANUAL;
    kind: typeof AI_CHAT_MESSAGE_KIND.MANUAL_FILTERS;
}

export interface AiChatErrorMessage {
    id: string;
    kind: typeof AI_CHAT_MESSAGE_KIND.ERROR;
    content: string;
    failedInput: string | null;
}

export type AiChatMessage =
    | AiChatTextMessage
    | AiChatFiltersMessage
    | AiChatQueryResultMessage
    | AiChatDataCollectionPickerMessage
    | AiChatManualFiltersMessage
    | AiChatErrorMessage;

export type AiChatFeedbackableMessage =
    | AiChatAssistantTextMessage
    | AiChatFiltersMessage
    | AiChatQueryResultMessage;

// Feedback submission state

export interface AiChatFeedbackSubmission {
    status: OperationStatus;
    messageId: string | null;
    errorMessage: string | null;
}

// Active-chat payload built from a selected history item.
export interface AiChatActivePayload {
    chatId: string;
    messages: AiChatMessage[];
    dataCollectionId: number | null;
}

export const QUERY_BUILDER_MODES = {
    AI: 'ai',
    MANUAL: 'manual',
} as const;

export type QueryBuilderMode = (typeof QUERY_BUILDER_MODES)[keyof typeof QUERY_BUILDER_MODES];
